<template>
  <v-dialog v-model="dialog" scrollable max-width="1000px">
    <template v-slot:activator="{ on }">
      <a class="link" v-on="on">{{ object && object['NUM_CLIENTES'] ? object['NUM_CLIENTES'] : 0 | localeString(0) }}</a>
    </template>
    <v-card v-if="dialog" class="retencao-dialog-card">
      <v-card-title>
        <v-container fluid wrap class="pa-0">
          <v-row>
            <p class="title mb-4 primary-text">Detalhe do 'coorte'</p>
            <v-spacer />
            <v-menu v-if="hasPiiAccess" bottom origin="center center" transition="scale-transition">
              <template v-slot:activator="{ on }">
                <mf-button :loading="csv.loading" type="icon" icon="more_vert" color="cinza-0" v-on:click.native="on.click" />
              </template>

              <v-list>
                <v-list-item @click="exportarCsvAtivos">
                  <v-list-item-title>Exportar Ativos em CSV</v-list-item-title>
                </v-list-item>
                <v-list-item @click="exportarCsvInativos">
                  <v-list-item-title>Exportar Inativos em CSV</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-row>

          <v-row>
            <v-col xs4>
              <span class="primary-text body-2 bold">Grupo de 'coorte'</span>
              <span class="group-box">
                <span class="body-2 white--text bold">
                  {{ groupDates | dateMonth }}
                </span>
              </span>
            </v-col>
            <v-col xs4>
              <span class="primary-text body-2 bold">Período</span>
              <span class="retidos-box">
                <span class="body-2 bold secondary-text">
                  {{ date | dateMonth }}
                </span>
              </span>
            </v-col>
          </v-row>
          <v-divider />
          <v-row>
            <v-col xs3>
              <p class="body-1 primary-text">Clientes do grupo de 'coorte'</p>
              <v-chip label>{{ firstObject['NUM_CLIENTES'] | localeString(0) }}</v-chip>
              <mf-quick-actions
                :quick-action-group-id="`cohort-${groupDates}`"
                :short-description="`Retenção ${groupDates} - Coorte`"
                :long-description="`Clientes que fizeram a primeira compra em ${groupDates}`"
                :segmentation-group="cohortSegmentationGroup"
                :disabled="loading"
              />
            </v-col>
            <v-col xs3>
              <p class="body-1 primary-text">Clientes ativos</p>
              <v-chip label> {{ object['NUM_CLIENTES'] | localeString(0) }} - {{ percentualAtivos | localeString(1) | percent }} </v-chip>
              <mf-quick-actions
                :quick-action-group-id="`cohort-${groupDates}-${date}-active`"
                :short-description="`Retenção ${groupDates} - Ativos em ${date}`"
                :long-description="`Clientes que fizeram a primeira compra em ${groupDates} e compraram em ${date}`"
                :segmentation-group="cohortActiveSegmentationGroup"
                :disabled="loading"
              />
            </v-col>
            <v-col xs3>
              <p class="body-1 primary-text">Clientes inativos</p>
              <v-chip label> {{ quantidadeInativos | localeString(0) }} - {{ percentualInativos | localeString(1) | percent }} </v-chip>
              <mf-quick-actions
                :quick-action-group-id="`cohort-${groupDates}-${date}-inactive`"
                :short-description="`Retenção ${groupDates} - Inativos em ${date}`"
                :long-description="`Clientes que fizeram a primeira compra em ${groupDates} e não compraram em ${date}`"
                :segmentation-group="cohortInactiveSegmentationGroup"
                :disabled="loading"
              />
            </v-col>
          </v-row>
        </v-container>
      </v-card-title>
      <v-divider />
      <v-card-text class="pa-0">
        <v-tabs v-if="hasPiiAccess" v-model="activeTab" background-color="white" slider-color="primary" grow>
          <v-tab ripple>Clientes ativos</v-tab>
          <v-tab ripple :disabled="isFirst">Clientes inativos</v-tab>
          <v-tab-item :transition="false" :reverse-transition="false">
            <v-divider />
            <v-data-table :headers="headers" :items="clientesAtivos" :loading="loading" :items-per-page-options="rowsPerPageArray">
              <template v-slot:item="{ item }">
                <tr>
                  <td>{{ item['COD_ID_CLIENTE'] }}</td>
                  <td>{{ item['NOM_NOME'] }}</td>
                  <td>{{ item['DES_FONE_1'] | telefone }}</td>
                  <td>{{ item['DES_FONE_2'] | telefone }}</td>
                  <td>{{ item['DES_EMAIL'] }}</td>
                </tr>
              </template>
            </v-data-table>
          </v-tab-item>
          <v-tab-item :transition="false" :reverse-transition="false">
            <v-divider />
            <v-data-table :headers="headers" :items="clientesInativos" :loading="loading" :items-per-page-options="rowsPerPageArray">
              <template v-slot:item="{ item }">
                <tr>
                  <td>{{ item['COD_ID_CLIENTE'] }}</td>
                  <td>{{ item['NOM_NOME'] }}</td>
                  <td>{{ item['DES_FONE_1'] | telefone }}</td>
                  <td>{{ item['DES_FONE_2'] | telefone }}</td>
                  <td>{{ item['DES_EMAIL'] }}</td>
                </tr>
              </template>
            </v-data-table>
          </v-tab-item>
        </v-tabs>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from 'vuex'
import { assertArray } from '@/config/helpers.js'
import { listagemClientesRetencaoAtivosCSV, listagemClientesRetencaoInativosCSV } from '@/services/kpis.js'
import { criteriaGroup, listOfIdsCriteria } from '@/components/quick-actions/segmentationGroups.js'
import { DateTime } from 'luxon'

export default {
  filters: {
    dateMonth(value) {
      return DateTime.fromFormat(value, 'MM/yyyy').toFormat('MMM/yy')
    },
  },
  props: {
    filter: Object,
    object: Object,
    firstObject: Object,
    date: String,
    groupDates: String,
    index: Number,
  },
  data: () => ({
    dialog: false,
    clientesAtivos: [],
    clientesInativos: [],
    activeTab: 0,
    loading: false,
    rowsPerPageArray: [10, 25, 50, 100, { text: 'Todos', value: -1 }],
    headers: [
      {
        text: 'ID',
        width: '10%',
        value: 'COD_ID_CLIENTE',
      },
      {
        text: 'Nome',
        width: '30%',
        value: 'NOM_NOME',
      },
      {
        text: 'Celular',
        width: '20%',
        value: 'DES_FONE_1',
      },
      {
        text: 'Telefone',
        width: '20%',
        value: 'DES_FONE_2',
      },
      {
        text: 'E-mail',
        width: '20%',
        value: 'DES_EMAIL',
      },
    ],
    total: null,
    csv: {
      loading: false,
    },
  }),
  computed: {
    linhaFilter() {
      return this.object['NUM_GRUPO_PRIMEIRA_COMPRA']
    },
    colunaFilter() {
      return this.object['NUM_DT_COMPRA']
    },
    localFilter() {
      return {
        num_group: this.linhaFilter,
        num_dt: this.colunaFilter,
        ...this.filter,
      }
    },
    percentualAtivos() {
      return (this.object['NUM_CLIENTES'] / this.firstObject['NUM_CLIENTES']) * 100
    },
    percentualInativos() {
      return (this.quantidadeInativos / this.firstObject['NUM_CLIENTES']) * 100
    },
    quantidadeInativos() {
      return this.firstObject['NUM_CLIENTES'] - this.object['NUM_CLIENTES']
    },
    isFirst() {
      return Boolean(this.index === 0)
    },
    activeClientIds() {
      return this.clientesAtivos.map(({ COD_ID_CLIENTE }) => COD_ID_CLIENTE)
    },
    inactiveClientIds() {
      return this.clientesInativos.map(({ COD_ID_CLIENTE }) => COD_ID_CLIENTE)
    },
    cohortSegmentationGroup() {
      return criteriaGroup([listOfIdsCriteria(this.activeClientIds.concat(this.inactiveClientIds), 'Retenção - ativos e inativos')])
    },
    cohortActiveSegmentationGroup() {
      return criteriaGroup([listOfIdsCriteria(this.activeClientIds, 'Retenção - ativos')])
    },
    cohortInactiveSegmentationGroup() {
      return criteriaGroup([listOfIdsCriteria(this.inactiveClientIds, 'Retenção - inativos')])
    },
  },
  watch: {
    dialog(value) {
      if (value) {
        this.getDataFromApi()
      } else {
        this.clientesAtivos = []
        this.clientesInativos = []
      }
    },
  },
  methods: {
    ...mapActions(['listagemClientesRetencaoAtivos', 'listagemClientesRetencaoInativos']),
    getDataFromApi() {
      this.loading = true

      Promise.all([this.listagemClientesRetencaoAtivos(this.localFilter), this.listagemClientesRetencaoInativos(this.localFilter)]).then((response) => {
        this.clientesAtivos = assertArray(response[0])
        this.clientesInativos = assertArray(response[1])
        this.loading = false
      })
    },
    exportarCsvAtivos() {
      this.csv.loading = true
      let dateNow = DateTime.now().toFormat('dd-MM-yy')
      listagemClientesRetencaoAtivosCSV({ send_file: true, ...this.localFilter }, `retencao-ativos-${dateNow}`).then(() => {
        this.csv.loading = false
      })
    },
    exportarCsvInativos() {
      this.csv.loading = true
      let dateNow = DateTime.now().toFormat('dd-MM-yy')
      listagemClientesRetencaoInativosCSV({ send_file: true, ...this.localFilter }, `retencao-inativos-${dateNow}`).then(() => {
        this.csv.loading = false
      })
    },
  },
}
</script>
<style lang="scss">
.retencao-dialog-card {
  p {
    margin-bottom: 0;
  }
  .group-box {
    text-align: center;
    background-color: $marca-0;
    border-radius: 3px;
    padding: 8px 16px;
    margin: 8px;
  }
  .retidos-box {
    text-align: center;
    background-color: $cinza-l80;
    border-radius: 3px;
    padding: 8px;
    margin: 8px;
  }
  .ativos-chip {
    background-color: #b2f2bb;
  }
  .inativos-chip {
    background-color: #ffc9c9;
  }
  .grupo-chip {
    background-color: #ffec99;
  }
}
</style>
